import { type Post, PostStatus } from '@src/store/contentEntities/model';
import { type QueryConstraint, type QueryDocumentSnapshot, orderBy, where } from 'firebase/firestore/lite';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { createCollectionAtomFamily, createLoadMoreAtom, fetchEntities, fetchEntity } from '../common/collectionAtom';
import { setMultiple, setSingle } from '../common/common';
import { createMapAtom } from '../common/mapAtom';
import type { ContentEntity } from '../contentEntities/model';
import { jotaiStore } from '../store';

export type PostCollectionType =
    | 'partner'
    | 'pinned'
    | 'user'
    | 'community'
    | 'recommended'
    | 'pending'
    | 'deleted'
    | 'scheduled'
    | 'draft'
    | 'premiumDraft'
    | 'premiumScheduled'
    | 'communityScheduled'
    | 'communityDraft'
    | 'userDeleted'
    | 'userDraft'
    | 'userPremiumDraft'
    | 'userCommunityDraft'
    | 'userScheduled'
    | 'userPending'
    | 'userPremiumScheduled'
    | 'userCommunityScheduled';

const postsAtom = createMapAtom<Post>({
    initialValue: {},
    debugLabel: 'postsAtom',
});

export const findPostByIdentifier = (identifier: string): Post | undefined => {
    const posts = getPosts();
    for (const post of Object.values(posts)) {
        if (post.id === identifier || post.slugUrl === identifier) return post;
    }
    return;
};

export const postAtomFamily = atomFamily((postId: string | undefined) => {
    const derivedAtom = atom<Post | undefined>(get => (postId ? get(postsAtom)[postId] : undefined));
    derivedAtom.debugLabel = `postAtomFamily/${postId}`;
    return derivedAtom;
});

export const loadPostByIdentifierAtom = atom(null, async (get, set, identifier: string) => {
    const cachedPost = findPostByIdentifier(identifier);
    if (cachedPost) return cachedPost;

    // Determine if identifier is likely an ID or a slug
    const looksLikeId = identifier.length === 20 && /^[a-zA-Z0-9]+$/.test(identifier);
    let post: ContentEntity | undefined;

    if (looksLikeId) {
        post = await fetchEntity('Post', identifier);
    } else {
        // Fetch by slugUrl using fetchEntities
        const { entitiesData } = await fetchEntities<Post>('Post', {
            fetchLimit: 1,
            queryConstraints: [where('slugUrl', '==', identifier)],
        });
        post = Object.values(entitiesData)[0];
    }

    if (!post) throw new Error(`Post not found: ${identifier}`);

    setPost(post as Post);
    return post as Post;
});
loadPostByIdentifierAtom.debugLabel = 'loadPostByIdentifierAtom';

export function getPostsAtom() {
    return postsAtom;
}

export function getPosts() {
    return jotaiStore.get(postsAtom);
}

export function getPost(postId: string): Post | undefined {
    return getPosts()[postId];
}

export function setPost(post: Post) {
    setSingle(post, postsAtom);
}

export function setPosts(posts: Record<string, Post>) {
    setMultiple(posts, postsAtom);
}

export interface PostCollectionCompositeKeyParams {
    type: PostCollectionType;
    tokenName: string;
    flairId?: string;
    userId?: string;
}

export function generatePostCollectionKey(params: PostCollectionCompositeKeyParams): string {
    let key = `${params.type}_${params.tokenName}`;
    if (params.flairId) key += `_flair-${params.flairId}`;
    if (params.userId) key += `_user-${params.userId}`;
    return key;
}

export const postCollectionAtomFamily = createCollectionAtomFamily<PostCollectionCompositeKeyParams, Post>({
    generateKey: generatePostCollectionKey,
    equalityCheck: (a, b) => generatePostCollectionKey(a) === generatePostCollectionKey(b),
    debugLabel: 'postCollectionState',
});

const deleteStatuses = [PostStatus.DELETED_BY_DOMAIN_ADMIN, PostStatus.DELETED_BY_CATEGORY_ADMIN, PostStatus.DELETED_BY_TOKEN_ADMIN];

const getQueryConstraints = (params: PostCollectionCompositeKeyParams): QueryConstraint[] => {
    const { type, tokenName, flairId, userId } = params;
    const constraints: QueryConstraint[] = [where('tokenName', '==', tokenName)];

    switch (type) {
        case 'partner':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('kind', '==', 'story'), orderBy('createdAt', 'desc'));
            if (flairId) constraints.push(where('flairIds', 'array-contains', flairId));
            break;
        case 'community':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('kind', '==', 'community'), orderBy('createdAt', 'desc'));
            if (flairId) constraints.push(where('flairIds', 'array-contains', flairId));
            break;
        case 'pinned':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('isPinned', '==', true), orderBy('pinnedTime', 'desc'));
            break;
        case 'recommended': {
            const lastWeek = new Date();
            lastWeek.setDate(lastWeek.getDate() - 7);
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('createdAt', '>', lastWeek), orderBy('numComments', 'desc'));
            break;
        }
        case 'user':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.PUBLISHED, PostStatus.PUBLISHED_UNLISTED]), orderBy('createdAt', 'desc'));
            break;
        case 'userDeleted':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', deleteStatuses), orderBy('createdAt', 'desc'));
            break;
        case 'userDraft':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.DRAFT]), orderBy('createdAt', 'desc'));
            break;
        case 'userPremiumDraft':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.DRAFT]), where('kind', '==', 'story'), orderBy('createdAt', 'desc'));
            break;
        case 'userCommunityDraft':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.DRAFT]), where('kind', '==', 'community'), orderBy('createdAt', 'desc'));
            break;
        case 'userScheduled':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]), orderBy('scheduledAt', 'asc'));
            break;
        case 'userPremiumScheduled':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(
                where('publisherId', '==', userId),
                where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]),
                where('kind', '==', 'story'),
                orderBy('scheduledAt', 'asc'),
            );
            break;
        case 'userCommunityScheduled':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(
                where('publisherId', '==', userId),
                where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]),
                where('kind', '==', 'community'),
                orderBy('scheduledAt', 'asc'),
            );
            break;
        case 'userPending':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', 'in', [PostStatus.PENDING]), orderBy('createdAt', 'desc'));
            break;
        case 'pending':
            constraints.push(where('status', 'in', [PostStatus.PENDING]), orderBy('createdAt', 'desc'));
            break;
        case 'scheduled':
            constraints.push(where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]), orderBy('scheduledAt', 'asc'));
            break;
        case 'draft': // any draft posts (community or story)
            constraints.push(where('status', 'in', [PostStatus.DRAFT]), orderBy('createdAt', 'desc'));
            break;
        case 'premiumDraft':
            constraints.push(where('status', 'in', [PostStatus.DRAFT]), where('kind', '==', 'story'), orderBy('createdAt', 'desc'));
            break;
        case 'communityDraft':
            constraints.push(where('status', 'in', [PostStatus.DRAFT]), where('kind', '==', 'community'), orderBy('createdAt', 'desc'));
            break;
        case 'premiumScheduled':
            constraints.push(where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]), where('kind', '==', 'story'), orderBy('scheduledAt', 'asc'));
            break;
        case 'communityScheduled':
            constraints.push(where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]), where('kind', '==', 'community'), orderBy('scheduledAt', 'asc'));
            break;
        case 'deleted': {
            constraints.push(where('status', 'in', deleteStatuses), orderBy('createdAt', 'desc'));
            break;
        }
        default:
            ((_: never) => {
                throw new Error(`Unsupported post type: ${type}`);
            })(type);
    }

    return constraints;
};

export type LoadMorePostsParams = PostCollectionCompositeKeyParams & { fetchLimit?: number };
export const loadMorePostsAtom = createLoadMoreAtom<Post, PostCollectionCompositeKeyParams>(postCollectionAtomFamily, 'Post', getQueryConstraints, postsAtom);

export function resetPostCollection(params: PostCollectionCompositeKeyParams) {
    const collectionAtom = postCollectionAtomFamily(params);
    jotaiStore.set(collectionAtom, draft => {
        draft.ids = [];
        draft.lastDoc = null;
        draft.hasMore = true;
        draft.status = 'idle';
        draft.error = null;
    });
}

export async function fetchPostByByExternalArticleId(articleId: string): Promise<Post | undefined> {
    const { entitiesData } = await fetchEntities('Post', {
        fetchLimit: 1,
        queryConstraints: [where('externalArticleId', '==', articleId)],
    });
    const post = Object.values(entitiesData)[0];
    return post as Post | undefined;
}
